import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i2 from '@nexa/configuration';
import { map as map$1 } from 'rxjs/internal/operators/map';
import { BehaviorSubject, from } from 'rxjs';
import * as signalR from '@microsoft/signalr';

/**
 * Implementa todas las operaciones base a nivel de Api
 */
class BaseApiService {
    /**
     * Crea una nueva instancia de @see BaseApiService
     * @param httpClient Service HttpClient
     * @param configurationService Service NexaConfigurationService
     * @param api Name Api Configuration
     * @param service Nombre del controlador o complemento del servicio base (https://domain.com/api/{service}/....)
     */
    constructor(httpClient, configurationService, api, service) {
        this.configurationService = configurationService;
        this.api = api;
        this.service = service;
        this.httpClient = httpClient;
    }
    /**
     * Metodo encargado de consultar todos los registros
     * @param args Argumentos extra para la consulta de información
     * @returns Retorna un array de tipo @see TDto con la información de los registros
     * @example
     * // Get all records
     * this.service.all().suscribe(x => console.log(x))
     */
    all(args) {
        return this.getUri((api) => this.httpClient.get(`${api}/All`));
    }
    /**
     * Metodo encargado de consultar los registros en formato de diccionario
     * @param state Estado del registro a consultar
     * @param args Argumentos extra para la consulta de información
     * @returns Retorna un objeto de tipo @see TDto
     * @example
     * // Get records with state true
     * this.service.keyValue(true).suscribe(x => console.log(x))
     * // Get records with state false
     * this.service.keyValue(false).suscribe(x => console.log(x))
     */
    keyValue(state, args) {
        return this.getUri((api) => {
            const url = state === true || state === false ? `${api}/KeyValue?state=${state}` : `${api}/KeyValue`;
            return this.httpClient.get(url);
        });
    }
    /**
     * Metodo encargado de consultar el detalle de un registro
     * @param id Id del registro a buscar
     * @param args Argumentos extra para la consulta de información
     * @returns Retorna un objeto de tipo @see TDto
     * @example
     * // Find record with id 1
     * this.service.find(1).suscribe(x => console.log(x))
     */
    find(id, args) {
        return this.getUri((api) => this.httpClient.get(`${api}/${id}`));
    }
    /**
     * Metodo encargado de consultar los registros paginandolos
     * @param pagin Parametros de la paginación
     * @param args Argumentos extra para la consulta de información
     * @returns Objeto de tipo @see PageResult con la información
     * @example
     * // Get records paginated from server
     * this.service.pagination({ pageSize: 10, pageNumber: 1 }).suscribe(x => console.log(x))
     */
    pagination(pagin, args) {
        return this.getUri((api) => this.httpClient.get(`${api}/Pagination?PageSize=${pagin.pageSize}&PageNumber=${pagin.pageNumber}`));
    }
    /**
     * Metodo encargado de persistir la información del registro
     * @param data Información del registro a persistir
     * @param args Argumentos extra para la consulta de información
     * @returns Objeto del tipo @see TDto con la información del registro creado
     * @example
     * // Create record
     * this.service.create({ ...TDto }).suscribe(x => console.log(x))
     */
    create(data, args) {
        return this.getUri((api) => this.httpClient.post(api, data));
    }
    /**
     * Metodo encargado de actualizar la información del registro
     * @param id Id del registro a actualizar
     * @param data Información a actualizar
     * @param args Argumentos extra para la consulta de información
     * @returns Valor booleano que indica el proceso de la transacción
     * @example
     * // Update record with id 1
     * this.service.update(1, { ...TDto }).suscribe(x => console.log(x))
     */
    update(id, data, args) {
        return this.getUri((api) => this.httpClient.put(`${api}/${id}`, data));
    }
    /**
     * Metodo encargado de cambiar el estado del registro
     * @param id Id del registro a actualizar
     * @param state Estado a asignar
     * @param args Argumentos extra para la consulta de información
     * @returns Valor booleano que indica el proceso de la transacción
     * @example
     * // Change state to false with id 1
     * this.service.changeState(1, false).suscribe(x => console.log(x))
     * // Change state to true with id 1
     * this.service.changeState(1, true).suscribe(x => console.log(x))
     */
    changeState(id, state, args) {
        return this.getUri((api) => this.httpClient.put(`${api}/ChangeState?id=${id}&state=${state}`, null));
    }
    /**
     * Metodo encargado de eliminar un registro
     * @param id Id del registro a eliminar
     * @param args Argumentos extra para la consulta de información
     * @returns Valor booleano que indica el proceso de la transacción
     * @example
     * // Delete record with id 1
     * this.service.delete(1).suscribe(x => console.log(x))
     */
    delete(id, args) {
        return this.getUri((api) => this.httpClient.delete(`${api}/${id}`));
    }
    /**
     * Flujo encargado obtner la api
     * @param observable Flujo a ejecutar
     * @example
     * // Uri to generate http request
     * this.service.getUri((api) => { ... }).suscribe(x => console.log(x))
     */
    getUri(fn) {
        return this.configurationService.configuration$.pipe(map((x) => x.apis[this.api]), map((api) => `${api}/${this.service}`), switchMap((api) => fn(api)));
    }
}

/**
 * Servicio encargado de interactuar con el microservicio de access control list
 */
class ActionApiService extends BaseApiService {
    /**
     * Crea una nueva instancia de @see ActionApiService
     * @param httpClient Service HttpClient
     * @param configuration Service NexaConfigurationService
     */
    constructor(httpClient, configuration) {
        super(httpClient, configuration, 'acl', 'Action');
    }
    /**
     * Metodo encargado de consultar la acción a partir del controlador y la acción
     * @param controller Id del cliente
     * @param action Estado del registro a consultar
     * @returns Retorna un objeto de tipo @see ActionDto
     * @example
     * // Filter by Controller and Action
     * this.service.getByRoute('controller', 'action').suscribe(x => console.log(x))
     */
    getByRoute(controller, action) {
        return this.getUri((api) => this.httpClient.get(`${api}/GetByRoute?controller=${controller}&action=${action}`));
    }
}
ActionApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActionApiService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
ActionApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActionApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActionApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Servicio encargado de comunicarse con el microservicio de directorio activo
 */
class ActiveDirectoryService {
    /**
     * Crea una nueva instancia de @see ActiveDirectoryService
     * @param httpClient Service HttpClient
     * @param configurationService Service NexaConfigurationService
     */
    constructor(httpClient, configurationService) {
        this.httpClient = httpClient;
        this.configurationService = configurationService;
    }
    /**
     * Flujo encargado obtner la api
     * @param observable Flujo a ejecutar
     */
    getUri(fn) {
        return this.configurationService.configuration$.pipe(map$1((x) => x.apis.ldap), map$1((api) => `${api}/ActiveDirectory`), switchMap(fn));
    }
    /**
     * Servicio encargado de consultar los path de la configuración
     * @returns Retorna un diccionario con los paths
     */
    paths() {
        return this.getUri((api) => this.httpClient.get(`${api}/Paths`));
    }
    /**
     * Servicio encargado de obtener las unidades organizacionales
     * @param path Unidad organizacional a buscar
     * @param loadChildrens True para retornar los hijos de la unidad organizacional
     * @returns Retorna las unidades organizacionales
     */
    organizationalUnit(path, loadChildrens = false) {
        return this.getUri((api) => this.httpClient.get(`${api}/OrganizationalUnit?path=${path}&loadChildrens=${loadChildrens}`));
    }
    /**
     * Servicio encargado de obtener los usuarios de un determinado paths
     * @param path Unidad organizacional a buscar
     * @returns Retorna los usuarios que se encuentren en el patth indicado
     */
    getAllUsers(path) {
        return this.getUri((api) => this.httpClient.get(`${api}/GetAllUsers?path=${path}`));
    }
    /**
     * Servicio que consultara el usuario del directorio filtrando por el atributo [cn]
     * @param fullName Nombre del usuario a buscar en el directorio activo
     * @returns Retorna una operación asincrona con la información del usuario identificado
     */
    userByFullName(fullName) {
        return this.getUri((api) => this.httpClient.get(`${api}/UserByFullName?fullName=${fullName}`));
    }
    /**
     * Servicio que consultara el usuario del directorio filtrando por el atributo [SAMAccountName]
     * @param loginName Usuario a buscar en el directorio activo
     * @returns Retorna la información del usuario identificado
     */
    userByLoginName(loginName) {
        return this.getUri((api) => this.httpClient.get(`${api}/UserByLoginName?loginName=${loginName}`));
    }
    /**
     * Servicio que consultara los usuarios que coincidan con el atributo [givenName]
     * @param givenName Valor por el cual se obtendra los usuarios (givenName)
     * @returns Retorna la información del usuario
     */
    usersByFirstName(givenName) {
        return this.getUri((api) => this.httpClient.get(`${api}/UsersByFirstName?givenName=${givenName}`));
    }
    /**
     * Servicio que consultara el grupo del directorio filtrando por el atributo [cn]
     * @param nombre Nombre del grupo
     * @returns Retorna una operación asincrona con la información del grupo identificado
     */
    groupByName(groupName) {
        return this.getUri((api) => this.httpClient.get(`${api}/GroupByName?groupName=${groupName}`));
    }
    /**
     * Servicio que consultara los usuarios que pertenecen al grupo. Para identificar el grupo se utiliza el atributo [SAMAccountName]
     * @param groupName Nombre del grupo que se buscara en el idrectorio activo
     * @returns Retorna una operación asincrona con la información del grupo identificado
     */
    userFromGroup(groupName) {
        return this.getUri((api) => this.httpClient.get(`${api}/UserFromGroup?groupName=${groupName}`));
    }
    /**
     * Servicio que consulta los grupos que coincidan con los primeros caracteres [cn=value*]
     * @param groupName Nombre del grupo que se buscara en el idrectorio activo
     * @return Returna la información de los usuarios que hacen parte del grupo
     */
    groupsByName(groupName) {
        return this.getUri((api) => this.httpClient.get(`${api}/GroupsByName?groupName=${groupName}`));
    }
    /**
     * Servicio que permite la creación de un usuario en el dominio
     * @param user Usuario que se creara en el directorio activo
     * @param password Contraseña a asingar al usuario
     * @return Retorna la inforamción del usuario creado
     */
    addUser(user, password) {
        return this.getUri((api) => this.httpClient.post(`${api}/AddUser?password=${password}`, user));
    }
    /**
     * Servicio encargado de agregar un grupo al usuario que se especifique
     * @param userName Usuario al que se le debe agregar el grupo
     * @param groupName Grupo a añadir al usuario
     * @returns Retorna true si el proceso es satisfactorio
     */
    addUserToGroup(userName, groupName) {
        return this.getUri((api) => this.httpClient.post(`${api}/AddUserToGroup?userName=${userName}&groupName=${groupName}`, null));
    }
    /**
     * Servicio encargado de remover el grupo al usuario que se especifique
     * @param userName Usuario al que se le removera el grupo
     * @param groupName Grupo a remover del usuario
     * @returns Retorna true si el proceso es satisfactorio
     */
    removeUserFromGroup(userName, groupName) {
        return this.getUri((api) => this.httpClient.delete(`${api}/RemoveUserFromGroup?userName=${userName}&groupName=${groupName}`));
    }
    /**
     * Servicio encargado de eliminar un usuario
     * @param userName Usuario a eliminar (SAMAccountName)
     * @returns Return true si el proceso es satisfactorio, de lo contrario retornar false sino se puede eliminar o si este no existe
     */
    deleteUser(userName) {
        return this.getUri((api) => this.httpClient.delete(`${api}/DeleteUser?userName=${userName}`));
    }
    /**
     * Servicio que permite actualizar el valor de un atributo de un usuario del dominio
     * @param property Atributo a actualizar LdapProperties tiene las constantes definidas)
     * @param value Valor a asignar al atributo
     * @param userName Usuario al cual se le realizara la actualización (SAMAccountName)
     * @param clear Indica al sistema si se eliminara o limpara el valor o valores antiguos del atributo o propiedad especificada en el
     *              parametro property antes de asignar el nuevo valor en la propiedad value
     * @returns Retorna true si el proceso es satisfactorio
     */
    updatePropertyByLoginName(property, value, userName, clear = true) {
        return this.getUri((api) => this.httpClient.put(`${api}/UpdatePropertyByLoginName?property=${property}&value=${value}&userName=${userName}&clear=${clear}`, null));
    }
    /**
     * Servicio que permite la creación de un usuario en el dominio
     * @param path Path del usuario donde se va a crear la entidad
     * @param user Usuario que contiene la información a actualizar
     * @returns Retorna el objeto actualizado
     */
    updateUser(path, user) {
        return this.getUri((api) => this.httpClient.put(`${api}/UpdateUser?userName=${path}`, user));
    }
    /**
     * Servicio encargado de validar si un usuario esta asignado a un grupo
     * @param userName Login or SAMAccountName
     * @param groupName Nombre del Grupo or cn
     * @returns Retorna true si el usuario pertenece al grupo
     */
    isUserInGroup(userName, groupName) {
        return this.getUri((api) => this.httpClient.post(`${api}/IsUserInGroup?userName=${userName}&groupName=${groupName}`, null));
    }
    /**
     * Servicio encargado de cambiar la contraseña del usuario
     * @param userName Usuario a asignar la contraseña
     * @param password Contraseña a asignar
     * @returns Retorna true si el proceso es satisfactorio
     */
    changePassword(userName, password) {
        return this.getUri((api) => this.httpClient.put(`${api}/ChangePassword?userName=${userName}&password=${password}`, null));
    }
    /**
     * Servicio encargado de validar las credenciales de un usuario
     * @param userName Usuario a validar contra el directorio activo
     * @param password Contraseña del usuario a validar contra el directorio activo
     * @returns Retorna true si el proceso es satisfactorio
     */
    validateCredentials(userName, password) {
        const uri = `/ValidateCredentials?userName=${userName}&password=${password}`;
        return this.getUri((api) => this.httpClient.post(`${api}${uri}`, null));
    }
}
ActiveDirectoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActiveDirectoryService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
ActiveDirectoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActiveDirectoryService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: ActiveDirectoryService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Servicio encargado de interactuar con el microservicio de access control list
 */
class AppApiService extends BaseApiService {
    /**
     * Crea una nueva instancia de @see AppApiService
     * @param httpClient Service HttpClient
     * @param configuration Service NexaConfigurationService
     */
    constructor(httpClient, configuration) {
        super(httpClient, configuration, 'acl', 'app');
    }
    /**
     * Metodo encargado de consultar una aplicación por su key
     * @param key Nombre clave de la aplicación
     * @returns Retorna el objeto que corresponda al key
     * @example
     * // Filter by key App
     * this.service.getByKey('nexa.microservice.api.key').suscribe(x => console.log(x))
     */
    getByKey(key) {
        return this.getUri((api) => this.httpClient.get(`${api}/GetByKey?key=${key}`));
    }
}
AppApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppApiService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
AppApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Servicio encargado de interactuar con el microservicio de access control list
 */
class AppPermissionApiService extends BaseApiService {
    /**
     * Crea una nueva instancia de @see AppPermissionApiService
     * @param httpClient Service HttpClient
     * @param configuration Service NexaConfigurationService
     */
    constructor(httpClient, configuration) {
        super(httpClient, configuration, 'acl', 'AppPermission');
    }
    /**
     * Metodo encargado de consultar los registros paginandolos
     * @param args Argumentos extra para la consulta de información
     * @param pagin Parametros de la paginación
     * @returns Objeto de tipo @see PageResult con la información
     * @example
     * // Filter by IdApp
     * this.service.pagination({ pageSize: 10, pageNumber: 1 }, { idApp: 1 }).suscribe(x => console.log(x))
     */
    pagination(pagin, args) {
        return this.getUri((api) => {
            const data = {
                idApp: 0,
                ...args,
            };
            return this.httpClient.get(`${api}/Pagination/${data.idApp}?PageSize=${pagin.pageSize}&PageNumber=${pagin.pageNumber}`);
        });
    }
    /**
     * Metodo encargado de consultar una aplicación por su key
     * @param key Nombre clave de la aplicación
     * @returns Retorna una lista de objetos que corresponda al key
     * @example
     * // Filter by key app
     * this.service.pagination('nexa.microservice.api.key').suscribe(x => console.log(x))
     */
    getByApp(key) {
        return this.getUri((api) => this.httpClient.get(`${api}/GetByApp?appKey=${key}`));
    }
    /**
     * Metodo encargado de persistir la información de los registros
     * @param data Información de los registros a persistir
     * @returns Objeto del tipo con la información del registro creado
     * @example
     * // Create Range AppPermissions
     * this.service.createRange([{...dto1, ...dto2 }]).suscribe(x => console.log(x))
     */
    createRange(data) {
        return this.getUri((api) => this.httpClient.post(`${api}/CreateRange`, data));
    }
}
AppPermissionApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppPermissionApiService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
AppPermissionApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppPermissionApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AppPermissionApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Servicio encargado de interactuar con el microservicio de access control list
 */
class PermissionApiService extends BaseApiService {
    /**
     * Crea una nueva instancia de @see PermissionApiService
     * @param httpClient Service HttpClient
     * @param configuration Service NexaConfigurationService
     */
    constructor(httpClient, configuration) {
        super(httpClient, configuration, 'acl', 'Permission');
    }
}
PermissionApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: PermissionApiService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
PermissionApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: PermissionApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: PermissionApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Servicio encargado de interactuar con el microservicio de access control list
 */
class RolePermissionApiService extends BaseApiService {
    /**
     * Crea una nueva instancia de @see RolePermissionApiService
     * @param httpClient Service HttpClient
     * @param configuration Service NexaConfigurationService
     */
    constructor(httpClient, configuration) {
        super(httpClient, configuration, 'acl', 'RolePermission');
    }
    /**
     * Metodo encargado de consultar los registros paginandolos
     * @param pagin Parametros de la paginación
     * @param args Argumentos extra para la consulta de información
     * @returns Objeto de tipo @see PageResult con la información
     * // Filter by IdApp and Role
     * this.service.pagination({ pageSize: 10, pageNumber: 1 }, { idApp: 1, role: 'group' }).suscribe(x => console.log(x))
     */
    pagination(pagin, args) {
        return this.getUri((api) => {
            const data = {
                idApp: 0,
                role: '',
                ...args,
            };
            return this.httpClient.get(`${api}/Pagination/${data.idApp}/${data.role}?PageSize=${pagin.pageSize}&PageNumber=${pagin.pageNumber}`);
        });
    }
    /**
     * Metodo encargado de consultar los permisos por el nombre del role y el key de la aplicación
     * @param role Nombre del role
     * @param appKey Nombre clave de la aplicación
     * @returns Retorna un objeto de tipo @see ActionDto
     * @example
     * // Get by role
     * this.service.getByRole('app.key', 'agent').suscribe(x => console.log(x))
     */
    getByRole(appKey, role) {
        return this.getUri((api) => this.httpClient.get(`${api}/GetByRole?appKey=${appKey}&role=${role}`));
    }
    /**
     * Metodo encargado de persistir la información de los registros
     * @param data Información de los registros a persistir
     * @returns Objeto del tipo @see TDto con la información del registro creado
     * @example
     * // Create Range RolePermission
     * this.service.createRange([{...dto1, ...dto2 }]).suscribe(x => console.log(x))
     */
    createRange(data) {
        return this.getUri((api) => this.httpClient.post(`${api}/CreateRange`, data));
    }
}
RolePermissionApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: RolePermissionApiService, deps: [{ token: i1.HttpClient }, { token: i2.NexaConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
RolePermissionApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: RolePermissionApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: RolePermissionApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.NexaConfigurationService }]; } });

/**
 * Información para registra una función en un determinado evento
 */
class Listener {
    /**
     * Crea una nueva instancia de @see Listener
     */
    constructor(name, value) {
        this.name = name;
        this.behaviorSubject = new BehaviorSubject(value);
    }
    /**
     * Nombre de la función a escuchar
     */
    get Name() {
        return this.name;
    }
    /**
     * Observable que notifica el dato recibido desde el web sockeet
     */
    get listener$() {
        return this.behaviorSubject.asObservable();
    }
    /**
     * Metodo encargado de disparar la notificación del evento
     * @param data Información a notificar en el observable
     */
    next(data) {
        this.behaviorSubject.next(data);
    }
}

/**
 * Servicio base para construir hubs que interactuaran con el Microserivio
 */
class BaseHubService {
    /**
     * Crea una nueva instancia de @see BaseHubService
     * @param configurationService Service NexaConfigurationService
     * @param api Name Api Configuration
     * @param hub Nombre del hub o complemento del servicio base (https://domain.com/api/{service}/....)
     */
    constructor(configurationService, api, hub) {
        this.configurationService = configurationService;
        this.api = api;
        this.hub = hub;
        /**
         * Lista de funciones a invocar
         */
        this.listeners = [];
    }
    /**
     * Inicia la conexión con SignalaR
     * @returns Promise
     */
    initialize(nameGroup) {
        return this.getUri()
            .pipe(map((uri) => {
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(uri, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
                .withAutomaticReconnect()
                .build();
            return connection;
        }), tap((connection) => {
            if (this.configurationService.isProduction()) {
                connection.onclose((e) => console.log(`Close: ${e}`));
                connection.onreconnected((connectionId) => console.log(`Reconnected Connection Id: ${connectionId}`));
                connection.onreconnecting((e) => console.log(`Reconnecting: ${e}`, e));
            }
        }), tap((connection) => {
            this.connection = connection;
            this.listeners.forEach(listener => {
                this.connection.on(listener.Name, (data) => listener.next(data));
            });
        }), switchMap((connection) => {
            const start$ = from(connection.start())
                .pipe(tap(() => {
                console.log(`Connection success`);
                connection.invoke('JoinRoom', nameGroup)
                    .then(() => console.log(`JoinRoom - ${nameGroup}`))
                    .catch((error) => console.log(`Error: ${error}`));
                connection.invoke('GetConnectionId')
                    .then(connectionId => this.connectionId = connectionId)
                    .catch((error) => console.log(`Error: ${error}`));
            }, error => {
                console.log(`Connection error: ${error}`);
            }));
            return start$;
        }));
    }
    /**
     * Metodo encargado de registrar una función a invocar
     * @param fn Función a registrar
     */
    addListener(listener) {
        this.listeners.push(listener);
    }
    /**
     * Metodo encargado de cerrar la conexión con el web socket
     */
    closeConnection() {
        return from(this.connection.stop());
    }
    /**
     * Flujo encargado obtner la api
     * @param observable Flujo a ejecutar
     * @example
     * // Uri to generate http request
     * this.service.getUri((api) => { ... }).suscribe(x => console.log(x))
     */
    getUri() {
        return this.configurationService.configuration$
            .pipe(map((x) => x.apis[this.api]), map((api) => api.replace('api', 'hubs')), map((api) => `${api}/${this.hub}`));
    }
}

/*
 * Public API Surface of apis
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActionApiService, ActiveDirectoryService, AppApiService, AppPermissionApiService, BaseApiService, BaseHubService, Listener, PermissionApiService, RolePermissionApiService };
